import { DATA_SOURCE_IDS } from './dataInputModel';

export const MAX_IMPORT_FILE_BYTES = 25 * 1024 * 1024;
export const MAX_SCOPE3_AF_TOTAL_FILE_BYTES = 60 * 1024 * 1024;

export const ACTIVE_IMPORT_STATUSES = new Set(['QUEUED', 'PROCESSING']);
export const TERMINAL_IMPORT_STATUSES = new Set(['SUCCESS', 'FAILED', 'PARTIAL']);
export const STRUCTURED_IMPORT_RESULT_STATUSES = new Set([
  'SUCCESS', 'FAILED', 'PARTIAL', 'CANCELLED',
]);

export function validateImportFile(file) {
  if (!file) return { valid: false, code: 'FILE_REQUIRED' };
  if (!String(file.name || '').toLowerCase().endsWith('.xlsx')) {
    return { valid: false, code: 'FILE_TYPE_INVALID' };
  }
  if (!Number.isFinite(file.size) || file.size <= 0) {
    return { valid: false, code: 'FILE_EMPTY' };
  }
  if (file.size > MAX_IMPORT_FILE_BYTES) {
    return { valid: false, code: 'FILE_TOO_LARGE' };
  }
  return { valid: true, code: null };
}

export function validateScope3AfFiles(files) {
  const selectedFiles = Array.isArray(files) ? files : [];
  const totalBytes = selectedFiles.reduce((total, file) => total + (file?.size || 0), 0);
  if (totalBytes > MAX_SCOPE3_AF_TOTAL_FILE_BYTES) {
    return { valid: false, code: 'FILE_TOO_LARGE' };
  }
  for (const file of selectedFiles) {
    const validation = validateImportFile(file);
    if (!validation.valid) return validation;
  }
  return { valid: true, code: null };
}

export function isActiveImportStatus(status) {
  return ACTIVE_IMPORT_STATUSES.has(String(status || '').toUpperCase());
}

export function isTerminalImportStatus(status) {
  return TERMINAL_IMPORT_STATUSES.has(String(status || '').toUpperCase());
}

export function canCancelKnownImport(knownImport, operations, operationId) {
  if (!operationId) return false;
  if (knownImport?.id === operationId) return knownImport.canCancel === true;
  return Object.values(operations || {}).some(operation => (
    operation?.id === operationId && operation.canCancel === true
  ));
}

export function normalizeDeletionDate(value) {
  const match = String(value || '').trim().match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year
    || date.getUTCMonth() !== month - 1
    || date.getUTCDate() !== day) {
    return null;
  }
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function createDeletionPayload(sourceId, values) {
  if (sourceId === DATA_SOURCE_IDS.SCOPE_2_ELECTRICITY) {
    return {
      source: sourceId,
      from_month: Number(values.fromMonth),
      to_month: Number(values.toMonth),
    };
  }
  return {
    source: sourceId,
    from_date: normalizeDeletionDate(values.fromDate),
    to_date: normalizeDeletionDate(values.toDate),
  };
}

export function validateDeletionSelection(sourceId, fiscalYear, values) {
  if (sourceId === DATA_SOURCE_IDS.SCOPE_2_ELECTRICITY) {
    const fromMonth = Number(values.fromMonth);
    const toMonth = Number(values.toMonth);
    if (!Number.isInteger(fromMonth) || !Number.isInteger(toMonth)
      || fromMonth < 1 || toMonth > 12) {
      return 'DELETE_MONTH_REQUIRED';
    }
    return fromMonth <= toMonth ? null : 'DELETE_RANGE_INVALID';
  }

  const fromDate = String(values.fromDate || '');
  const toDate = String(values.toDate || '');
  if (!fromDate || !toDate) return 'DELETE_DATE_REQUIRED';
  const normalizedFrom = normalizeDeletionDate(fromDate);
  const normalizedTo = normalizeDeletionDate(toDate);
  if (!normalizedFrom || !normalizedTo) return 'DELETE_DATE_INVALID';
  // Guard against a date outside the selected reporting year even when the user
  // types it manually instead of using the bounded native picker.
  if (normalizedFrom.slice(0, 4) !== String(fiscalYear)
    || normalizedTo.slice(0, 4) !== String(fiscalYear)) {
    return 'DELETE_DATE_OUT_OF_YEAR';
  }
  return normalizedFrom <= normalizedTo ? null : 'DELETE_RANGE_INVALID';
}
